import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { BalanceDisplay, FightCard, MyBetsList } from '../components/betting';
import { Leaderboard, ParticipantBetHistory } from '../components/leaderboard';
import { ParlayWidget } from '../components/parlay/ParlayWidget';
import { useEvent, useUser, useAuth } from '../store';
import { loadEvent as loadEventFromStorage, saveCurrentUser, mergeTemplateIntoEvent } from '../utils';
import { useEventPolling } from '../hooks';
import type { Participant } from '../types';

/**
 * Dashboard Page
 * Participant dashboard with balance, fights, bets, and leaderboard
 */
export function DashboardPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, leaderboard, getParticipant } = useEvent();
  const { currentParticipant: currentParticipantFromUser, setCurrentParticipant } = useUser();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'fights' | 'bets' | 'leaderboard'>('fights');
  const [selectedParticipantForHistory, setSelectedParticipantForHistory] = useState<Participant | null>(null);

  // Get current participant from event context (always up-to-date with bets)
  // Priority: 1. Find by ID in event, 2. Find by userId in event, 3. Find by username in event, 4. Fallback to user context
  const currentParticipant = (() => {
    if (!event) return currentParticipantFromUser;
    
    // Try to find by participant ID
    if (currentParticipantFromUser?.id) {
      const byId = event.participants.find(p => p.id === currentParticipantFromUser.id);
      if (byId) return byId;
    }
    
    // Try to find by userId
    if (currentUser?.id) {
      const byUserId = event.participants.find(p => p.userId === currentUser.id);
      if (byUserId) return byUserId;
    }
    
    // Try to find by username match
    if (currentUser?.username) {
      const byUsername = event.participants.find(
        p => p.name.toLowerCase() === currentUser.username.toLowerCase()
      );
      if (byUsername) return byUsername;
    }
    
    // Fallback to user context (might not have bets)
    return currentParticipantFromUser;
  })();
  
  // Try to find participant by username if not set
  useEffect(() => {
    if (!currentParticipant && event && currentUser && !loading) {
      const foundParticipant = event.participants.find(
        p => p.name.toLowerCase() === currentUser.username.toLowerCase()
      );
      
      if (foundParticipant) {
        console.log('Found participant by username:', foundParticipant.name);
        setCurrentParticipant(foundParticipant);
        if (eventId) {
          saveCurrentUser(eventId, foundParticipant.id);
        }
      }
    }
  }, [event, currentUser, currentParticipant, loading, setCurrentParticipant, eventId]);

  // Sync currentParticipant from event context
  useEffect(() => {
    if (currentParticipant && currentParticipantFromUser && currentParticipant.id === currentParticipantFromUser.id) {
      // Update UserContext with latest participant data from EventContext
      if (JSON.stringify(currentParticipant.bets) !== JSON.stringify(currentParticipantFromUser.bets) ||
          currentParticipant.totalAvailable !== currentParticipantFromUser.totalAvailable) {
        setCurrentParticipant(currentParticipant);
      }
    }
  }, [currentParticipant, currentParticipantFromUser, setCurrentParticipant]);

  // Load event from Firestore
  useEffect(() => {
    if (!eventId) {
      navigate('/');
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      
      try {
        let eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          // Merge template fights if this is an event instance
          eventData = await mergeTemplateIntoEvent(eventData);
          loadEvent(eventData);
        } else {
          navigate('/');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, loadEvent, navigate]);

  // Redirect if no participant (with delay to allow participant finding)
  useEffect(() => {
    if (!loading && !currentParticipant && event && currentUser) {
      // Check one more time if user is a participant
      const isParticipant = event.participants.some(
        p => p.name.toLowerCase() === currentUser.username.toLowerCase()
      );
      
      if (!isParticipant) {
        // User is definitely not a participant
        console.log('User not a participant, redirecting to join');
        navigate(`/join/${eventId}`);
      }
    }
  }, [loading, currentParticipant, eventId, navigate, event, currentUser]);

  // Poll for event updates (every 5 seconds)
  useEventPolling({
    eventId: eventId || null,
    enabled: !loading && !!eventId && !!currentParticipant,
    interval: 5000,
    onUpdate: (_updatedEvent) => {
      // Event updated - state will be updated automatically by the hook
      console.log('Event updated via polling');
    },
    onError: (error) => {
      console.error('Error polling event:', error);
    },
  });

  const handlePropsClick = (fightId: string) => {
    navigate(`/event/${eventId}/props/${fightId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!event || !currentParticipant) {
    return null; // Will redirect
  }

  const participantBets = currentParticipant.bets || [];
  const upcomingFights = event.fights.filter((f) => f.status === 'upcoming');
  const liveFights = event.fights.filter((f) => f.status === 'live');
  const completedFights = event.fights.filter((f) => f.status === 'completed');

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-xl font-bold text-primary-text">{event.name}</h1>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/screencast/${eventId}`)}
            >
              Public Leaderboard
            </Button>
          </div>
          <p className="text-sm text-secondary-text">
            {currentParticipant.name}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Balance Display */}
        <BalanceDisplay participant={currentParticipant} />

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('fights')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'fights'
                ? 'text-primary-text border-b-2 border-primary-blue'
                : 'text-secondary-text hover:text-primary-text'
            }`}
          >
            Fights
          </button>
          <button
            onClick={() => setActiveTab('bets')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'bets'
                ? 'text-primary-text border-b-2 border-primary-blue'
                : 'text-secondary-text hover:text-primary-text'
            }`}
          >
            My Bets ({participantBets.length})
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'leaderboard'
                ? 'text-primary-text border-b-2 border-primary-blue'
                : 'text-secondary-text hover:text-primary-text'
            }`}
          >
            Leaderboard
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'fights' && (
          <div className="space-y-4">
            {upcomingFights.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-primary-text mb-3">
                  Upcoming Fights
                </h2>
                <div className="space-y-4">
                        {upcomingFights.map((fight) => (
                          <FightCard
                            key={fight.id}
                            fight={fight}
                            onPropsClick={handlePropsClick}
                            canBet={currentParticipant.totalAvailable > 0}
                          />
                        ))}
                </div>
              </div>
            )}

            {liveFights.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-primary-text mb-3">
                  Live Fights
                </h2>
                <div className="space-y-4">
                  {liveFights.map((fight) => (
                    <FightCard
                      key={fight.id}
                      fight={fight}
                      canBet={false}
                    />
                  ))}
                </div>
              </div>
            )}

            {completedFights.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-primary-text mb-3">
                  Completed Fights
                </h2>
                <div className="space-y-4">
                  {completedFights.map((fight) => (
                    <FightCard
                      key={fight.id}
                      fight={fight}
                      canBet={false}
                    />
                  ))}
                </div>
              </div>
            )}

            {event.fights.length === 0 && (
              <Card padding="lg">
                <div className="text-center py-8">
                  <p className="text-secondary-text">No fights added yet</p>
                  <p className="text-muted-text text-sm mt-2">
                    Admin needs to add fights to this event
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'bets' && (
          <MyBetsList bets={participantBets} fights={event.fights} />
        )}

        {activeTab === 'leaderboard' && (
          <div>
            <h2 className="text-lg font-semibold text-primary-text mb-4">
              Leaderboard
            </h2>
            <p className="text-sm text-secondary-text mb-4">
              Ranked by winnings balance. Only money won from bets counts!
            </p>
            <Leaderboard 
              entries={leaderboard} 
              currentParticipantId={currentParticipant?.id}
              onUserClick={(participantId) => {
                const participant = getParticipant(participantId);
                if (participant) {
                  setSelectedParticipantForHistory(participant);
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Parlay Widget */}
      <ParlayWidget />

      {/* Participant Bet History Modal */}
      {selectedParticipantForHistory && (
        <ParticipantBetHistory
          participant={selectedParticipantForHistory}
          fights={event.fights}
          onClose={() => setSelectedParticipantForHistory(null)}
        />
      )}
    </div>
  );
}

